/**
 * Billing history models for resource billing tracking
 */

/**
 * Billed type. Numeric values intentionally match the API's BilledType enum ordinals
 * (Billed, Nonbilled, Probono, BusinessDevelopment, Absence, Firm) so the value sent to and
 * received from the API is identical on both sides.
 */
export enum BilledType {
  Billable = 0,
  NonBillable = 1,
  ProBono = 2,
  BusinessDevelopment = 3,
  Absence = 4,
  Firm = 5,
}

/**
 * Display labels for each billed type, keyed by the BilledType enum value.
 * Shared by every billing surface so labels stay consistent.
 */
export const BILLED_TYPE_LABELS: Record<BilledType, string> = {
  [BilledType.Billable]: 'Billable',
  [BilledType.NonBillable]: 'Non-Billable',
  [BilledType.ProBono]: 'Pro Bono',
  [BilledType.BusinessDevelopment]: 'Business Development',
  [BilledType.Absence]: 'Absence',
  [BilledType.Firm]: 'Firm',
};

/**
 * Resolve the display label for a billed type, returning '-' for unknown/missing values.
 */
export function getBilledTypeLabel(billedType: BilledType | null | undefined): string {
  if (billedType == null) {
    return '-';
  }
  return BILLED_TYPE_LABELS[billedType as BilledType] ?? '-';
}

/**
 * Format billed hours for display across the billing surfaces. Any value that
 * rounds to zero at one-decimal precision renders an en dash ('–') — so a tiny
 * non-zero value never shows a bare '0'; otherwise the value to one decimal,
 * with trailing zeros optionally stripped. Single source of truth so the
 * resource and matter cards render hours identically.
 *
 * @param stripTrailingZeros `true` for table cells ("6", "12.5"); omit/`false`
 *   for the breakdown and total tiles, which keep one decimal ("6.0").
 */
export function formatBillingHours(
  value: number,
  options?: { stripTrailingZeros?: boolean },
): string {
  const rounded = Number(value.toFixed(1));
  if (rounded <= 0) {
    return '–';
  }
  return options?.stripTrailingZeros ? rounded.toString() : rounded.toFixed(1);
}

/**
 * Hours worked under a single task code on one billing entry. Mirrors the server's
 * `BillingTaskHoursDto`. These are the feed's own per-code totals — nothing is summed on our
 * side, since an entry is exactly one record from the feed.
 *
 * **A breakdown does not account for all of an entry's hours.** The feed guarantees only that
 * task hours sum to *at most* the entry's `totalHours`. The difference is hours it did not break
 * out, and it is a normal state, not an error. Anything rendering this as a decomposition of
 * `totalHours` (a stacked bar, a percent-of-total column) must account for that remainder —
 * `totalHours - sum(taskSummary.totalHours)` — or it will understate.
 *
 * That guarantee holds for the feed's figures, not for the rounded ones the API returns. Each
 * bucket and the entry's `totalHours` are rounded to one decimal separately, so the buckets can
 * add up to slightly more than the total: tasks of 0.45 and 0.55 on a 1.00 entry arrive as
 * 0.5 + 0.6 against 1.0. Clamp the remainder at zero.
 *
 * Three states are distinct: a null `taskSummary` (the feed described no task hours for this
 * entry), a bucket with no usable `taskCode` (time the feed did not attribute to a code), and
 * the remainder above (the feed said nothing about those hours).
 *
 * `taskCode` and `taskDescription` arrive exactly as the feed sent them, so a null and a blank
 * stay distinct values. Whether that difference is worth showing is a display decision and may
 * differ per field — the breakdown panel renders one glyph for either kind of missing code,
 * while wording the description differently for each.
 */
export interface BillingTaskHours {
  /** Task code, e.g. "L240". Null for time recorded without one — the untasked bucket. */
  taskCode: string | null;
  /** Task description, e.g. "Dispositive Motions". Null whenever `taskCode` is. */
  taskDescription: string | null;
  totalHours: number;
}

export interface BillingEntry {
  startDate: string;
  endDate: string;
  totalHours: number;
  billedType: BilledType;
  /**
   * This entry's own task breakdown — scoped to this entry, so it lines up with the
   * `totalHours` beside it. See `BillingTaskHours`; it does not account for all of them.
   */
  taskSummary?: BillingTaskHours[] | null;
}

export interface BillingHistoryRequest {
  employeeId: string;
  startDate: string;
  endDate: string;
  /** Optional billed-type filter. Omit (undefined) to request all types. */
  billedTypes?: BilledType;
}

export interface BillingMatterData {
  matterName: string;
  clientName?: string;
  clientMatter: string;
  clientMatterNumber?: string;
  totalHours: number;
  totalBilledHours?: number;
  totalNonbilledHours?: number;
  totalProbonoHours?: number;
  totalBusinessDevelopmentHours?: number;
  totalAbsenceHours?: number;
  totalFirmHours?: number;
  /**
   * Set only on the flattened table rows, copied from the row's own `BillingEntry`. The API's
   * matter objects do not carry this — the breakdown lives on entries, since a row is one
   * bill-type entry rather than a whole matter. Display behind the `task-breakdowns` flag.
   */
  taskSummary?: BillingTaskHours[] | null;
  entries?: BillingEntry[];
  billedType?: BilledType;
  isSkeleton?: boolean; // Flag to identify skeleton rows during loading
}

export interface BillingHistoryResponse {
  requestedEmployeeId: string;
  requestedStartDate?: string;
  requestedEndDate?: string;
  requestedBilledTypes?: BilledType;
  matters: BillingMatterData[];
  totalHours?: number;
  totalBilledHours?: number;
  totalNonbilledHours?: number;
  totalProbonoHours?: number;
  totalBusinessDevelopmentHours?: number;
  totalAbsenceHours?: number;
  totalFirmHours?: number;
  /** Mean billed hours across the last six *completed* months. Zero-hour months count toward the average; the in-progress month is never included. */
  monthlyAverageBilledHours?: number;
  /** Billed hours summed over the completed months of the current calendar year. Excludes the in-progress month, so this is 0 for the whole of January. */
  currentYearToDateBilledHours?: number;
  /** Forecast of total billed hours by end of the current calendar year: the year-to-date figure plus monthlyAverageBilledHours for the current month and every month after it. */
  forecastedBilledHoursThisYear?: number;
  /** Forecast of billed hours over the next 12 months: monthlyAverageBilledHours x 12. */
  forecasted12MonthBilledHours?: number;
  /** Billed hours per month for the current calendar year (12 entries, Jan→Dec by index).
   *  Completed months have a value; current + future months are null (forecast from monthlyAverageBilledHours). */
  monthlyBilledHours?: (number | null)[];
  /** Running total of billed hours for the current calendar year (12 entries, Jan→Dec by index):
   *  actuals through the last completed month, then the forecast. The server rounds each point from
   *  the unrounded running total, so the last completed month equals currentYearToDateBilledHours and
   *  December equals forecastedBilledHoursThisYear. Chart running totals from this, not by summing
   *  monthlyBilledHours, whose separately rounded months can add up a few tenths away. */
  cumulativeBilledHours?: number[];
}

/**
 * Stable keys for the per-type breakdown tiles / bar segments. Must line up
 * exactly with the `type-{{ key }}` CSS classes and `--billing-type-color`
 * selectors in the component SCSS.
 */
export type BillingBreakdownKey =
  | 'billable'
  | 'non-billable'
  | 'pro-bono'
  | 'business-dev'
  | 'absence'
  | 'firm';

/**
 * A single billing-type tile / distribution-bar segment derived from the
 * response totals. Presentation-only; recomputed when the totals change.
 */
export interface BillingBreakdownTile {
  /** Stable key used to pick the type color class in the template. */
  key: BillingBreakdownKey;
  /** Short display label (abbreviated to fit the compact tile row). */
  label: string;
  /** True when this type has no hours in the selected range. */
  isZero: boolean;
  /** Hours to one decimal, or an en dash when zero. */
  valueDisplay: string;
  /** Whole-percent share of the grand total ('<1%' for sub-1% shares), or '' when zero. */
  percentDisplay: string;
  /** Share of the grand total, used for the distribution-bar segment width. */
  widthPercent: number;
}

/**
 * Discriminates how a date-range option resolves to an actual start/end window.
 * - `rolling`: a fixed number of days back from today (uses `days`).
 * - `last7`: today minus 6 → today (a 7-day inclusive window).
 * - `lastCalendarWeek`: the most recent completed Sun–Sat.
 * - `yearToDate`: Jan 1 of the current year → today.
 * - `lastCalendarYear`: Jan 1 → Dec 31 of the previous year.
 * - `custom`: an arbitrary user-supplied range.
 */
export type DateRangeKind =
  | 'rolling'
  | 'last7'
  | 'lastCalendarWeek'
  | 'yearToDate'
  | 'lastCalendarYear'
  | 'custom';

export interface DateRangeOption {
  /** Stable id used for persistence + selection matching. */
  readonly key: string;
  readonly label: string;
  readonly kind: DateRangeKind;
  /** Retained for rolling options & legacy compatibility (-1 = custom). */
  readonly days: number;
}

export const DATE_RANGE_OPTIONS: readonly DateRangeOption[] = [
  { key: 'last7', label: 'Last 7 Days', kind: 'last7', days: 7 },
  { key: 'last30', label: 'Last 30 Days', kind: 'rolling', days: 30 },
  { key: 'last60', label: 'Last 60 Days', kind: 'rolling', days: 60 },
  { key: 'last90', label: 'Last 90 Days', kind: 'rolling', days: 90 },
  {
    key: 'lastCalendarWeek',
    label: 'Last Calendar Week',
    kind: 'lastCalendarWeek',
    days: 0,
  },
  { key: 'yearToDate', label: 'Year-To-Date', kind: 'yearToDate', days: 0 },
  {
    key: 'lastCalendarYear',
    label: 'Last Calendar Year',
    kind: 'lastCalendarYear',
    days: 0,
  },
  { key: 'custom', label: 'Custom', kind: 'custom', days: -1 }, // -1 indicates custom range
];

/**
 * Default selection when a user has no saved preference. Pinned to "Last 30 Days"
 * (not the first array entry) so reordering the dropdown doesn't change the default.
 */
export const DEFAULT_DATE_RANGE_OPTION: DateRangeOption =
  DATE_RANGE_OPTIONS.find((opt) => opt.key === 'last30') ??
  DATE_RANGE_OPTIONS[0];

/**
 * Format a Date as a local `yyyy-MM-dd` string. Uses local date parts (not
 * `toISOString()`, which converts to UTC and can shift calendar boundaries such
 * as Jan 1, Dec 31, or a week's Sunday by a day).
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Resolve a date-range option to concrete local `yyyy-MM-dd` start/end strings.
 *
 * @param option The selected range option.
 * @param custom Custom start/end dates, used only when `option.kind === 'custom'`.
 * @param referenceDate Optional "today" override (primarily for tests).
 */
export function computeDateRange(
  option: DateRangeOption,
  custom?: { startDate: Date | null; endDate: Date | null },
  referenceDate: Date = new Date(),
): { startDate: string; endDate: string } {
  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  switch (option.kind) {
    case 'custom': {
      const start = custom?.startDate ?? today;
      const end = custom?.endDate ?? today;
      return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
    }
    case 'last7': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { startDate: formatLocalDate(start), endDate: formatLocalDate(today) };
    }
    case 'lastCalendarWeek': {
      // Most recent completed Saturday, then back to its Sunday.
      const lastSat = new Date(today);
      lastSat.setDate(lastSat.getDate() - (today.getDay() + 1));
      const start = new Date(lastSat);
      start.setDate(start.getDate() - 6);
      return {
        startDate: formatLocalDate(start),
        endDate: formatLocalDate(lastSat),
      };
    }
    case 'yearToDate': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { startDate: formatLocalDate(start), endDate: formatLocalDate(today) };
    }
    case 'lastCalendarYear': {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
    }
    case 'rolling':
    default: {
      const start = new Date(today);
      start.setDate(start.getDate() - option.days);
      return { startDate: formatLocalDate(start), endDate: formatLocalDate(today) };
    }
  }
}
